/**
 * 설교 영상 리스트 페이지
 */

import React, { useState } from 'react';
import styled from '@emotion/styled';
import { MainLayout } from '../../../../shared/components';
import { theme } from '../../../../shared/styles/theme';
import { mediaQueries } from '../../../../shared/constants/breakpoints';
import { PlaylistType } from '../../services/youtubeService';
import { useSermons } from '../../../../shared/hooks';

interface SermonListProps {
  sermonType: PlaylistType;
  title: string;
}

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${theme.spacing.xxxl} ${theme.spacing.lg};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.md};
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xxxl};
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxxl};
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.fonts.heading};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.xxl};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.textLight};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

const PlaylistBanner = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
`;

const PlaylistTitle = styled.h2`
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin-bottom: ${theme.spacing.md};
`;

const PlaylistLink = styled.a`
  display: inline-block;
  background-color: white;
  color: ${theme.colors.primary};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.full};
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);
  }
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};

  ${mediaQueries.maxMobile} {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }
`;

const VideoCard = styled.div`
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const VideoInfo = styled.div`
  padding: ${theme.spacing.lg};
`;

const VideoTitle = styled.h3`
  font-size: ${theme.fontSizes.md};
  font-weight: 600;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const VideoDate = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textLight};
`;

const LoadingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};

  ${mediaQueries.maxMobile} {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }
`;

const SkeletonCard = styled.div`
  background: #f0f0f0;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`;

const SkeletonThumbnail = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonInfo = styled.div`
  padding: ${theme.spacing.lg};
`;

const SkeletonLine = styled.div<{ width?: string }>`
  height: 16px;
  background: #e0e0e0;
  border-radius: 4px;
  margin-bottom: ${theme.spacing.sm};
  width: ${props => props.width || '100%'};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background-color: ${props => props.disabled ? theme.colors.border : theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: background-color 0.2s ease-in-out;

  &:hover:not(:disabled) {
    background-color: ${theme.colors.secondary};
  }
`;

export const SermonList: React.FC<SermonListProps> = ({ sermonType, title }) => {
  const [currentPageToken, setCurrentPageToken] = useState<string>('');

  // React Query로 설교 영상 목록 조회 (자동 캐싱)
  const { data, isLoading } = useSermons(sermonType, { pageToken: currentPageToken });

  const videos = data?.videoList || [];
  const prevPageToken = data?.prevPageToken;
  const nextPageToken = data?.nextPageToken;

  const handleVideoClick = (videoUrl: string) => {
    window.open(videoUrl, '_blank', 'noopener,noreferrer');
  };

  const handlePrevPage = () => {
    if (prevPageToken) {
      setCurrentPageToken(prevPageToken);
    }
  };

  const handleNextPage = () => {
    if (nextPageToken) {
      setCurrentPageToken(nextPageToken);
    }
  };

  return (
    <MainLayout>
      <PageContainer>
        <PageHeader>
          <PageTitle>{title}</PageTitle>
          <PageSubtitle>하나님의 말씀을 듣고 은혜를 나누세요</PageSubtitle>
        </PageHeader>

        {sermonType === 'sunday-morning' && (
          <PlaylistBanner>
            <PlaylistTitle>🎬 유튜브 전체 재생목록 보기</PlaylistTitle>
            <PlaylistLink
              href="https://www.youtube.com/@WiryeSewoomChurch/playlists"
              target="_blank"
              rel="noopener noreferrer"
            >
              YouTube 재생목록 열기
            </PlaylistLink>
          </PlaylistBanner>
        )}

        {isLoading ? (
          <LoadingGrid>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonCard key={i}>
                <SkeletonThumbnail />
                <SkeletonInfo>
                  <SkeletonLine width="90%" />
                  <SkeletonLine width="60%" />
                </SkeletonInfo>
              </SkeletonCard>
            ))}
          </LoadingGrid>
        ) : (
          <VideoGrid>
            {videos.map((video, index) => (
              <VideoCard
                key={`${video.videoUrl}-${index}`}
                onClick={() => handleVideoClick(video.videoUrl)}
              >
                <Thumbnail
                  src={video.thumbnailUrl}
                  alt={video.title}
                  loading="lazy"
                />
                <VideoInfo>
                  <VideoTitle>{video.title}</VideoTitle>
                  <VideoDate>{new Date(video.regDt).toLocaleDateString('ko-KR')}</VideoDate>
                </VideoInfo>
              </VideoCard>
            ))}
          </VideoGrid>
        )}

        {(prevPageToken || nextPageToken) && (
          <PaginationContainer>
            <PaginationButton
              onClick={handlePrevPage}
              disabled={!prevPageToken}
            >
              이전 페이지
            </PaginationButton>
            <PaginationButton
              onClick={handleNextPage}
              disabled={!nextPageToken}
            >
              다음 페이지
            </PaginationButton>
          </PaginationContainer>
        )}
      </PageContainer>
    </MainLayout>
  );
};
